import { createIntl, createIntlCache } from 'react-intl';
// Only the default locale (en) is bundled as a static fallback. The active
// locale's messages are injected at startup via registerIntlMessages() from
// ClientProvider (which receives them from the server layout) — this keeps
// ar/fr translation JSON out of every user's JS bundle.
import enMessages from '../../../public/locales/en/translation.json';
import { getLocaleFromCookie } from './getLocaleFromCookie';

const cache = createIntlCache();

let cachedLang: string | null = null;
let cachedIntl: any = null;
let cachedMessages: Record<string, string> = {};

let registeredLocale: string | null = null;
let registeredMessages: Record<string, string> | null = null;

/**
 * Register the active locale's messages (called from ClientProvider on the
 * client). Server-side rendering keeps the previous behavior: getIntl() falls
 * back to the bundled en messages (getLocaleFromCookie returns the default
 * locale on the server anyway).
 */
export function registerIntlMessages(locale: string, msgs: Record<string, string>) {
  if (typeof window === 'undefined') return;
  if (registeredLocale === locale && registeredMessages === msgs) return;
  registeredLocale = locale;
  registeredMessages = msgs;
  // Invalidate the memoized intl instance so it picks up the new messages.
  cachedIntl = null;
  cachedLang = null;
  cachedMessages = {};
}

export function getIntl() {
  const lang = getLocaleFromCookie();

  if (cachedIntl && cachedLang === lang) {
    return cachedIntl;
  }

  const localeMessages =
    (lang === registeredLocale && registeredMessages) || enMessages;
  cachedIntl = createIntl({ locale: lang, messages: localeMessages }, cache);
  cachedLang = lang;
  cachedMessages = {};
  return cachedIntl;
}

export function getTranslatedMessage(id: string, defaultMessage: string = ""): string {
  const intl = getIntl();

  // Return the id itself if it's empty or not provided
  if (!id || id.trim() === "") {
    return defaultMessage || id || "";
  }

  if (cachedMessages[id]) {
    return cachedMessages[id];
  }
  const msg = intl.formatMessage({ id, defaultMessage });
  cachedMessages[id] = msg;
  return msg;
}

export function getArabicTranslatedMessage(id: string, defaultMessage: string = ""): string {
  const lang = getLocaleFromCookie();

  // If the current language is English, return the default message or id
  if (lang === 'en') {
    return defaultMessage || id;
  }

  return getTranslatedMessage(id, defaultMessage);
}
